// src/contexts/AuthContext.jsx - Apartment Management System
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { getAuthToken, getCurrentUser, isAuthenticated as checkAuthentication } from '../models/authModel';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Refs so the interceptor always sees current values without stale closures
  const isAuthRef = useRef(false);
  const suppressLogoutRef = useRef(false);

  useEffect(() => {
    isAuthRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = getAuthToken();
        const userData = getCurrentUser();

        if (token && userData) {
          // Serve cached local data first for instantly fast rendering
          setUser(userData);
          setIsAuthenticated(true);

          // Suppress interceptor-driven logout during background profile refresh.
          // A transient failure here (App Check, brief network error) must NOT kill
          // an otherwise valid session.
          suppressLogoutRef.current = true;
          try {
            const response = await api.get('/users/profile');
            if (response.data && response.data.success && response.data.user) {
              const freshUser = { ...userData, ...response.data.user };
              setUser(freshUser);
              localStorage.setItem('user', JSON.stringify(freshUser));
            }
          } catch (profileErr) {
            console.warn('[AuthContext] Background profile refresh failed:', profileErr?.response?.status, profileErr?.message);
            // Do NOT re-throw — a background refresh failure is non-fatal.
          } finally {
            suppressLogoutRef.current = false;
          }

        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // API interceptor for 401/403 responses.
  // Uses refs (not closure values) so it always sees current auth state.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          if (isAuthRef.current && !suppressLogoutRef.current) {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  // Check token expiration periodically
  useEffect(() => {
    const checkTokenInterval = setInterval(() => {
      if (isAuthenticated && !checkAuthentication()) {
        // Token expired, log out
        logout();
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkTokenInterval);
  }, [isAuthenticated]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;